// frontend/src/utils/shaping/shapingRules.js

const shapingRules = {
  greeting: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'friendly',
    variants: [
      'Hi there, {{userName}}! How’s your day going?',
      'Hello again! What’s on your mind today?',
      'Hey {{userName}} — always good to hear from you.',
    ],
  },

  goodbye: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'warm',
    variants: [
      "Take care, {{userName}}. I'm always here when you need me.",
      'Goodbye for now. Hope to talk again soon.',
      'Wishing you peace and good things until next time.',
    ],
  },

  nap: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'light',
    variants: [
      'Sweet dreams, {{userName}}. Talk to you soon.',
      'Hope your nap is restful and peaceful!',
      'Get some good rest — you deserve it.',
    ],
  },

  recall_request: {
    useLocal: false,
    shapingTier: 'short',
    tone: 'clear',
    promptInstruction:
      'Respond like a warm, grounded friend-assistant. Be direct and memory-aware. Keep it brief. Asking a specific follow-up is fine when the user is recalling a person or group.',
    specificVariants: [
      'Yes — I remember {{memoryName}} as your {{target}}.',
      'I remember {{memoryName}}. You’ve mentioned {{memoryName}} as your {{target}}.',
      'Yes, I remember {{memoryName}} as your {{target}}. Were you asking about {{memoryName}} specifically?',
    ],
    generalVariants: [
      'I don’t have your {{target}} saved clearly yet. You can tell me more and I’ll remember.',
      'I don’t have a specific {{target}} stored yet. Want to tell me who you mean?',
      'Not clearly yet — if you tell me their name, I can remember them.',
    ],
  },

  memory_recall: {
    useLocal: false,
    shapingTier: 'short',
    tone: 'warm',
    promptInstruction:
      'Respond like a warm, natural friend-assistant. Keep it direct, grounded, and conversational. Avoid therapy-style reflection unless the user is expressing clear grief or distress.',
    specificVariants: [
      'Yes — I remember {{memoryName}} as your {{target}}.',
      'I remember {{memoryName}}. You’ve told me {{memoryName}} is your {{target}}.',
      'Yes, {{memoryName}} — I remember them as your {{target}}.',
    ],
    generalVariants: [
      'I remember some of what you’ve shared, but I don’t have that pinned down clearly yet.',
      'I remember parts of that, but not clearly enough to be certain yet.',
      'Some of that sounds familiar, but I don’t want to pretend I remember more than I do.',
    ],
  },

  reminder_request: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'clear',
    variants: [
      'Got it — I’ve saved that as a reminder. 📝',
      'Noted. You can always ask me about it later.',
      'Okay! I’ll remember that for you.',
    ],
  },

  dream_note: {
    useLocal: true,
    shapingTier: 'mid',
    tone: 'reflective',
    variants: [
      'Dream noted. I’ve saved it for you.',
      'Got it — I’ve written that dream down.',
      'I’ve saved that dream for you in case you want to revisit it later.',
    ],
  },

  comfort: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'gentle',
    promptInstruction:
      'The user may be seeking comfort or reassurance. Respond with empathy and presence. Keep it brief. One validating sentence is enough. Do not ask a follow-up question unless the user sounds unresolved or asks for help.',
  },

  contextual_continuation: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'grounded',
    promptInstruction:
      'This is a continuation of an already anchored conversation about a known person or situation. Stay grounded and context-aware. Light warmth is appropriate. It is acceptable to acknowledge obvious real-world context such as distance, routine, or family closeness when relevant. Do not over-probe, do not shift topics, and do not escalate into therapy-style reflection.',
  },

  gratitude: {
    useLocal: false,
    shapingTier: 'short',
    tone: 'warm',
    promptInstruction:
      'The user expressed gratitude. Respond sincerely, warmly, and briefly. Do not over-probe.',
  },

  loss: {
    useLocal: false,
    shapingTier: 'full',
    tone: 'compassionate',
    promptInstruction:
      'The user expressed grief or loss. Respond with tenderness and presence. Avoid clichés. A deeper response is appropriate here. If it feels right, you may gently suggest supportive actions such as reaching out to trusted loved ones, engaging in comforting activities like quiet prayer, connecting with a faith-based community, taking a reflective walk outside, or spending a few moments in stillness — but only if it feels natural and supportive.',
  },

  sadness: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'compassionate',
    promptInstruction:
      'The user expressed sadness. Validate gently and keep the response proportional. Do not over-probe unless the sadness feels unresolved or acute.',
  },

  grief: {
    useLocal: false,
    shapingTier: 'full',
    tone: 'compassionate',
    promptInstruction:
      'The user expressed grief. Respond with deep compassion and emotional presence. Allow space for their feelings without rushing to fix them. A deeper response is appropriate here. If it feels appropriate, you may gently suggest comforting actions like quiet prayer, reaching out to supportive loved ones, visiting a faith-based group, taking time for personal reflection, or simply resting in a safe space — but only if it feels natural and caring.',
  },

  overwhelm: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'grounding',
    promptInstruction:
      'The user expressed feeling overwhelmed. Respond calmly and help them feel centered. Keep it simple, supportive, and not overly analytical. Avoid follow-up questions unless they clearly need help.',
  },

  faith_question: {
    useLocal: false,
    shapingTier: 'full',
    tone: 'respectful',
    promptInstruction:
      'The user is questioning faith or spiritual meaning. Respond openly and gently, without judgment. Encourage reflection, offer prayer, or share a meaningful quote from scripture if it feels supportive and appropriate.',
  },

  prayer_request: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'reverent',
    promptInstruction:
      'The user asked for a prayer. Respond respectfully with warmth and spiritual care. Keep it focused and sincere.',
  },

  hope: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'uplifting',
    promptInstruction:
      'The user expressed hope or uncertainty. Respond with gentle encouragement and inspiration without overpromising. Keep it brief and do not over-probe.',
  },

  forgiveness: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'soft',
    promptInstruction:
      'The user is struggling with forgiveness. Respond with warmth, compassion, and space for healing. Keep it measured and avoid sounding like a therapist.',
  },

  apology: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'accepting',
    promptInstruction:
      'The user apologized or expressed guilt. Respond with grace, warmth, and emotional safety. Keep it concise and reassuring.',
  },

  trust: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'reassuring',
    variants: [
      'You can trust me with whatever’s on your mind, {{userName}}.',
      'This space is always safe. I’m honored to be someone you can talk to.',
      'Thank you for opening up — I don’t take that lightly.',
    ],
  },

  affirmation: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'encouraging',
    variants: [
      "You're doing great, {{userName}}. Keep going!",
      'I believe in you, {{userName}} — you’ve got this.',
      'One step at a time. You’re stronger than you think.',
    ],
  },

  motivation: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'bold',
    variants: [
      'You’ve got what it takes, {{userName}}. Let’s go! 💪',
      'Today’s a fresh start — let’s make something great happen.',
      'You don’t have to move mountains — just take the next step.',
    ],
  },

  humor_request: {
    useLocal: true,
    shapingTier: 'mid',
    tone: 'playful',
    variants: [
      "Why don't skeletons fight each other? They don't have the guts. 🦴",
      "Parallel lines have so much in common… it’s a shame they’ll never meet.",
      'I told my computer I needed a break, and now it won’t stop sending me beach photos. 🏖️',
    ],
  },

  change: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'supportive',
    variants: [
      'Change can be uncomfortable, but growth lives there too.',
      'I’m here while everything shifts around you, {{userName}}.',
      'You’re adapting beautifully — give yourself credit.',
    ],
  },

  reconnect: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'welcoming',
    promptInstruction:
      'The user returned after time away. Respond warmly, with a sense of continuity and care, but keep it natural and not overly sentimental.',
  },

  checking_in: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'casual',
    variants: [
      'Hey there, {{userName}}. I’m here. Want to chat?',
      'You popped in just in time. What’s on your mind?',
      'Good timing, {{userName}} — I’m here.',
    ],
  },

  ambiguous_emotion: {
    useLocal: true,
    shapingTier: 'short',
    tone: 'gentle',
    variants: [
      'That kind of moment can catch you off guard. I’m here with you.',
      'Sounds like that landed harder than expected. I’m here.',
      'Those surprise emotions can be strong. I’m here with you.',
    ],
  },

  trivia_question: {
    useLocal: false,
    shapingTier: 'short',
    tone: 'factual',
    promptInstruction:
      'Answer the user’s trivia question clearly and concisely without speculation.',
  },

  trivia_answer: {
    useLocal: false,
    shapingTier: 'short',
    tone: 'confident',
    promptInstruction:
      'Respond with a direct and accurate answer to the trivia answer input.',
  },

  direction: {
    useLocal: false,
    shapingTier: 'mid',
    tone: 'reflective',
    promptInstruction:
      'The user expressed uncertainty about what to do next. Respond with supportive reflection and open-ended clarity prompts, but do not over-question.',
  },

  list_create: {
    useLocal: false,
    shapingTier: 'short',
    tone: 'organized',
    promptInstruction:
      'The user asked to create a list. Help organize their thoughts clearly.',
  },
};

export function getToneDescription(tone = 'neutral') {
  switch (tone) {
    case 'kind':
      return 'gentle and kind';
    case 'uplifting':
      return 'encouraging and hopeful';
    case 'warm':
      return 'warm and friendly';
    case 'snarky':
      return 'witty with a sharp edge';
    case 'sassy':
      return 'playful with attitude';
    case 'solemn':
      return 'quiet and serious';
    case 'grateful':
      return 'filled with heartfelt appreciation';
    default:
      return 'calm and neutral';
  }
}

export default shapingRules;